/**
 * TableSelectStep — DB-source equivalent of the folder pre-flight review.
 *
 * Renders the multi-select table picker + run-config form between
 * `/db-connect` and `/run`. Replaces the old MVP "auto-select every
 * discovered table at a fixed 100K cap" behaviour.
 *
 * Driven by useProfilerSession state: discoveredTables, selectedTableNames,
 * runConfig. Calls `confirmTableSelection()` on submit, which POSTs
 * /select-tables then /run with the configured sample size + flags.
 */

import { useMemo, useState } from "react";

import { cn } from "../../../lib/utils";
import type { DiscoveredTable, RunConfig } from "../hooks/useProfilerSession";

// Presets surfaced as quick-pick buttons next to the sample-row input.
// `null` is the sentinel for "All rows" — passed through to the backend as
// an absent `sample_rows`, which makes the profiler read every row.
export const SAMPLE_PRESETS: Array<{ label: string; value: number | null }> = [
  { label: "10K", value: 10_000 },
  { label: "100K", value: 100_000 },
  { label: "1M", value: 1_000_000 },
  { label: "All", value: null },
];

// Rough estimate: ~10K rows / second for profiling on a single worker.
// Used purely for an order-of-magnitude UI hint, not an SLA promise.
const ROWS_PER_SECOND = 10_000;

interface Props {
  sourceName: string;
  tables: DiscoveredTable[];
  selected: Set<string>;
  onToggle: (tableName: string) => void;
  onToggleAll: (selected: boolean) => void;
  runConfig: RunConfig;
  onRunConfigChange: (config: Partial<RunConfig>) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
  // Schema switch (#682) — omit/leave undefined for non-database sources,
  // where there's nothing to switch.
  sourceType?: string | null;
  currentSchema?: string | null;
  availableSchemas?: string[];
  isSwitchingSchema?: boolean;
  schemaSwitchError?: string | null;
  onSwitchSchema?: (schemaName: string) => void;
}

function formatRows(n: number | null | undefined): string {
  if (n == null || n < 0) return "—";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `~${Math.max(1, Math.round(seconds))}s`;
  if (seconds < 3600) return `~${Math.round(seconds / 60)} min`;
  return `~${(seconds / 3600).toFixed(1)} hr`;
}

export function TableSelectStep({
  sourceName,
  tables,
  selected,
  onToggle,
  onToggleAll,
  runConfig,
  onRunConfigChange,
  onConfirm,
  onCancel,
  isLoading,
  sourceType,
  currentSchema,
  availableSchemas,
  isSwitchingSchema,
  schemaSwitchError,
  onSwitchSchema,
}: Props) {
  const [filter, setFilter] = useState("");

  // Schemas dropdown: always include the currently-connected schema (even
  // before the background fetch lands, or if it's absent from what the
  // engine reports back — e.g. a schema created after this connection was
  // opened elsewhere), deduped, sorted for a stable order.
  const schemaOptions = useMemo(() => {
    const set = new Set(availableSchemas || []);
    if (currentSchema) set.add(currentSchema);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [availableSchemas, currentSchema]);
  const showSchemaPicker =
    sourceType === "database" && !!onSwitchSchema && schemaOptions.length > 0;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return tables;
    return tables.filter((t) => t.name.toLowerCase().includes(q));
  }, [tables, filter]);

  const allSelected =
    tables.length > 0 && tables.every((t) => selected.has(t.name));

  // Estimated volume: sum of min(sampleRows, rowEstimate) over tables whose row
  // count is known. When sampleRows is null ("All"), the row estimate is used
  // directly. Tables with a missing/failed estimate are EXCLUDED from the
  // number (counted separately) — the previous behaviour added the full sample
  // cap for them in capped mode but 0 under "All", which made a capped estimate
  // exceed the uncapped "All" total (impossible, since min(cap, n) <= n). Now
  // the estimate is always monotonic in the cap and never larger than "All".
  const { totalVolume, knownAnyRows, unsizedCount } = useMemo(() => {
    let total = 0;
    let known = false;
    let unsized = 0;
    for (const t of tables) {
      if (!selected.has(t.name)) continue;
      const est = t.row_count_estimate;
      if (est == null || est < 0) {
        unsized += 1;
        continue;
      }
      known = true;
      total +=
        runConfig.sampleRows == null
          ? est
          : Math.min(runConfig.sampleRows, est);
    }
    return { totalVolume: total, knownAnyRows: known, unsizedCount: unsized };
  }, [tables, selected, runConfig.sampleRows]);

  const estimatedSeconds = Math.ceil(totalVolume / ROWS_PER_SECOND);

  // Views + Salesforce-sync tables are deselected by default (see
  // isDefaultExcludedTable). Count them so the panel can explain why they're
  // absent from the estimate and the run.
  const { viewCount, syncCount } = useMemo(() => {
    let v = 0;
    let s = 0;
    for (const t of tables) {
      if (t.type === "view") v += 1;
      else if (/SF_SYNC|SYNC_TRGR/i.test(t.name || "")) s += 1;
    }
    return { viewCount: v, syncCount: s };
  }, [tables]);

  const excludedNote = [
    viewCount > 0 && `${viewCount} view${viewCount > 1 ? "s" : ""}`,
    syncCount > 0 &&
      `${syncCount} sync-trigger table${syncCount > 1 ? "s" : ""}`,
  ]
    .filter(Boolean)
    .join(" and ");

  const canStart = selected.size > 0 && !isLoading;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div>
        <h2 className="text-heading-2 text-[var(--foreground)] mb-1">
          Choose tables to profile
        </h2>
        <p className="text-body text-[var(--muted-foreground)]">
          Connected to{" "}
          <code className="text-xs bg-white/5 px-1 rounded">{sourceName}</code>.{" "}
          {tables.length} tables discovered — select which to profile and how.
        </p>
        {showSchemaPicker && (
          <div className="mt-3 flex items-center gap-2">
            <label
              htmlFor="profiler-schema-select"
              className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide"
            >
              Schema
            </label>
            <select
              id="profiler-schema-select"
              value={currentSchema || ""}
              disabled={isSwitchingSchema}
              onChange={(e) => onSwitchSchema?.(e.target.value)}
              className="px-2 py-1 rounded-md bg-white/5 border border-[var(--border)] text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)] disabled:opacity-50"
            >
              {schemaOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {isSwitchingSchema && (
              <span className="text-xs text-[var(--muted-foreground)]">
                Switching…
              </span>
            )}
            {schemaSwitchError && (
              <span className="text-xs text-red-400">{schemaSwitchError}</span>
            )}
          </div>
        )}
      </div>

      {/* Run-config + summary panel */}
      <div className="glass-surface rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide mb-2">
              Sample size cap (per table)
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {SAMPLE_PRESETS.map((p) => {
                const active = runConfig.sampleRows === p.value;
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => onRunConfigChange({ sampleRows: p.value })}
                    className={cn(
                      "px-3 py-1.5 rounded-md text-xs font-medium border transition-colors",
                      active
                        ? "bg-[var(--primary)] text-[var(--primary-foreground)] border-[var(--primary)]"
                        : "bg-white/5 text-[var(--muted-foreground)] border-[var(--border)] hover:text-[var(--foreground)]",
                    )}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[var(--muted-foreground)]">
              {runConfig.sampleRows == null
                ? "Profiling all rows — slow for large tables."
                : `Up to ${runConfig.sampleRows.toLocaleString()} rows per table.`}
            </p>
          </div>

          <div className="space-y-2">
            <Toggle
              label="Profile data quality rules"
              hint="Runs the engagement rule packs and the business-insight checks (orphan references, date order, sentinel values and similar). Turn off for a faster, statistics-only profile: the baseline quality statistics still run."
              checked={runConfig.includeDq}
              onChange={(v) => onRunConfigChange({ includeDq: v })}
            />
            <Toggle
              label="Detect primary / foreign keys"
              hint="Finds declared and likely keys from constraints, column names, types and uniqueness. Needed for orphan-reference checks and the relationship map."
              checked={runConfig.includeKeys}
              onChange={(v) => onRunConfigChange({ includeKeys: v })}
            />
            <Toggle
              label="Build the relationship map"
              hint={
                runConfig.includeKeys
                  ? "Draws likely foreign keys (confidence 0.8 or higher): a column named like a key in another table whose values are mostly found in it, checked on a sample of distinct values."
                  : "Requires key detection, which is turned off."
              }
              checked={runConfig.includeKeys && runConfig.includeRelationships}
              disabled={!runConfig.includeKeys}
              onChange={(v) => onRunConfigChange({ includeRelationships: v })}
            />
          </div>
        </div>

        <div className="glass-surface rounded-lg p-4 flex flex-col justify-center">
          <p className="text-xs text-[var(--muted-foreground)] uppercase tracking-wide mb-2">
            Estimated profiling
          </p>
          <p className="text-2xl font-semibold text-[var(--foreground)]">
            {formatRows(totalVolume)} rows
          </p>
          <p className="text-xs text-[var(--muted-foreground)] mt-1">
            across {selected.size} of {tables.length} tables{" "}
            {knownAnyRows ? `· ${formatDuration(estimatedSeconds)}` : ""}
          </p>
          {excludedNote && (
            <p className="text-[11px] text-[var(--muted-foreground)] mt-2">
              {excludedNote} {viewCount + syncCount === 1 ? "is" : "are"}{" "}
              deselected by default — not sized in the estimate and not
              profiled. Tick {viewCount + syncCount === 1 ? "it" : "any"} in the
              list to include {viewCount + syncCount === 1 ? "it" : "them"}.
            </p>
          )}
          {unsizedCount > 0 && (
            <p className="text-[11px] text-amber-300/70 mt-2">
              {unsizedCount} of {selected.size} selected{" "}
              {unsizedCount === 1 ? "table" : "tables"} couldn't be sized and{" "}
              {unsizedCount === 1 ? "is" : "are"} excluded from this estimate —
              the run will still profile {unsizedCount === 1 ? "it" : "them"}.
            </p>
          )}
        </div>
      </div>

      {/* Picker controls */}
      <div className="flex items-center justify-between gap-3">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter tables…"
          className="flex-1 max-w-sm px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]"
        />
        <div className="flex items-center gap-3">
          <span className="text-sm text-[var(--muted-foreground)]">
            {selected.size} of {tables.length} selected
          </span>
          <button
            type="button"
            onClick={() => onToggleAll(!allSelected)}
            className="text-xs text-[var(--primary)] hover:underline"
          >
            {allSelected ? "Clear" : "Select All"}
          </button>
        </div>
      </div>

      {/* Table list */}
      <div className="glass-surface rounded-xl overflow-hidden">
        <div className="grid grid-cols-[auto_2fr_1fr_1fr_1fr] gap-3 px-4 py-2 border-b border-[var(--border)] text-[11px] uppercase tracking-wide text-[var(--muted-foreground)]">
          <span />
          <span>Name</span>
          <span>Schema</span>
          <span className="text-right">Rows</span>
          <span className="text-right">Est. time</span>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-sm text-[var(--muted-foreground)] px-4 py-6 text-center">
              No tables match "{filter}".
            </p>
          ) : (
            filtered.map((t) => (
              <TableRow
                key={`${t.schema || "_"}.${t.name}`}
                table={t}
                selected={selected.has(t.name)}
                onToggle={() => onToggle(t.name)}
                sampleCap={runConfig.sampleRows}
              />
            ))
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={!canStart}
          className="px-6 py-2.5 rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isLoading
            ? "Starting…"
            : `Start Profile · ${selected.size} table${selected.size === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}

function TableRow({
  table,
  selected,
  onToggle,
  sampleCap,
}: {
  table: DiscoveredTable;
  selected: boolean;
  onToggle: () => void;
  sampleCap: number | null;
}) {
  const rows = table.row_count_estimate;
  const rowsForEstimate =
    sampleCap == null
      ? (rows ?? 0)
      : rows != null
        ? Math.min(sampleCap, rows)
        : sampleCap;
  const secs = Math.max(1, Math.ceil(rowsForEstimate / ROWS_PER_SECOND));

  return (
    <label className="grid grid-cols-[auto_2fr_1fr_1fr_1fr] gap-3 items-center px-4 py-2 border-b border-[var(--border)] last:border-0 cursor-pointer hover:bg-white/3 transition-colors">
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="rounded border-[var(--border)] bg-transparent accent-[var(--primary)]"
      />
      <div className="min-w-0">
        <p
          className="text-sm text-[var(--foreground)] truncate"
          title={table.name}
        >
          {table.name}
        </p>
        {table.type === "view" && (
          <span className="text-[11px] text-[var(--muted-foreground)]">
            view
          </span>
        )}
      </div>
      <span className="text-xs text-[var(--muted-foreground)] truncate">
        {table.schema || "—"}
      </span>
      <span className="text-xs text-[var(--foreground)] text-right tabular-nums">
        {formatRows(rows)}
      </span>
      <span className="text-xs text-[var(--muted-foreground)] text-right">
        {rows == null && sampleCap == null ? "—" : formatDuration(secs)}
      </span>
    </label>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-3 group ${
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 rounded border-[var(--border)] bg-transparent accent-[var(--primary)]"
      />
      <span className="flex-1">
        <span className="block text-sm text-[var(--foreground)] group-hover:text-[var(--primary)] transition-colors">
          {label}
        </span>
        {hint && (
          <span className="block text-[11px] text-[var(--muted-foreground)]">
            {hint}
          </span>
        )}
      </span>
    </label>
  );
}
